import { useEffect } from 'react';

/**
 * Hook to dynamically update the theme-color meta tag
 * Used for iOS status bar / notch area color
 *
 * @param color - The color to set (hex, rgb, etc.) or null to reset to default
 *
 * Usage:
 * ```tsx
 * // Set specific color
 * useThemeColor('#ff0000');
 *
 * // Blend primary color with opacity over white background
 * const blendedColor = blendColorWithOpacity(primaryColor, 0.4);
 * useThemeColor(blendedColor);
 * ```
 */
export function useThemeColor(color: string | null) {
	useEffect(() => {
		const metaTag = document.querySelector('meta[name="theme-color"]');
		if (!metaTag) return;

		// Store original color for cleanup
		const originalColor = metaTag.getAttribute('content');

		// Set new color if provided
		if (color) {
			metaTag.setAttribute('content', color);
		}

		// Cleanup: restore original color on unmount
		return () => {
			if (originalColor) {
				metaTag.setAttribute('content', originalColor);
			}
		};
	}, [color]);
}

/**
 * Blend a hex color with opacity over a background color
 * Useful for calculating the actual visible color when using opacity
 *
 * @param foregroundHex - Foreground color in hex (e.g., '#ff0000' or '#f00')
 * @param opacity - Opacity value between 0 and 1
 * @param backgroundHex - Background color in hex (default: white '#ffffff')
 * @returns Blended color in hex format
 */
export function blendColorWithOpacity(
	foregroundHex: string,
	opacity: number,
	backgroundHex: string = '#ffffff'
): string {
	// Parse hex to RGB
	const parseHex = (hex: string): { r: number; g: number; b: number } => {
		// Remove # if present
		hex = hex.replace('#', '');

		// Handle shorthand (e.g., #f00 -> #ff0000)
		if (hex.length === 3) {
			hex = hex
				.split('')
				.map((c) => c + c)
				.join('');
		}

		return {
			r: parseInt(hex.substring(0, 2), 16),
			g: parseInt(hex.substring(2, 4), 16),
			b: parseInt(hex.substring(4, 6), 16),
		};
	};

	// Convert RGB to hex
	const toHex = (r: number, g: number, b: number): string => {
		const componentToHex = (c: number) => {
			const hex = Math.round(c).toString(16);
			return hex.length === 1 ? '0' + hex : hex;
		};
		return '#' + componentToHex(r) + componentToHex(g) + componentToHex(b);
	};

	const fg = parseHex(foregroundHex);
	const bg = parseHex(backgroundHex);

	// Blend: result = fg * opacity + bg * (1 - opacity)
	const r = fg.r * opacity + bg.r * (1 - opacity);
	const g = fg.g * opacity + bg.g * (1 - opacity);
	const b = fg.b * opacity + bg.b * (1 - opacity);

	return toHex(r, g, b);
}
